import type { MakeRequest, Plugin, Request } from '@tinkoff/request-core';
import request from '@tinkoff/request-core';
import http, {
  isAbortError,
  isHttpError,
  isTimeoutError,
} from '@tinkoff/request-plugin-protocol-http';
import validate from '@tinkoff/request-plugin-validate';

import { getUrlFromParams } from './get-url-from-params';

import type { ScpClient } from '..';
import {
  AbortError,
  HttpError,
  ResponseFormatError,
  TimeoutError,
} from '../../../errors/error';
import formatErrorPlugin from '../../plugins/format-error';
import type { ResponseSuccess } from '../../response/response';
import { isErrorResponse, isScpResponse } from '../../response/response';
import type { RequestParams } from '../request-params';

interface Options {
  plugins?: Plugin[];
}

export class WebRequestClient implements ScpClient {
  makeRequest: MakeRequest;

  constructor(options: Options = {}) {
    this.makeRequest = request([
      ...(options.plugins ?? []),
      formatErrorPlugin({
        errorFormatter({ error, response }) {
          if (isAbortError(error)) {
            return new AbortError(error);
          }
          if (isTimeoutError(error)) {
            return new TimeoutError(error);
          }
          if (isHttpError(error)) {
            if (isScpResponse(response) && isErrorResponse(response)) {
              return new HttpError({ status: error.status, ...response.error });
            }

            if (error.status) {
              return new HttpError(error);
            }
          }
          return;
        },
      }),
      validate({
        validator({ response }) {
          if (!isScpResponse(response)) {
            return new ResponseFormatError(response);
          }
          return;
        },
      }),
      http(),
    ]);
  }

  call<T>(params: RequestParams): Promise<ResponseSuccess<T>> {
    const url = getUrlFromParams(params);

    const requestParams: Request = {
      httpMethod: 'POST',
      type: params.type ?? 'json',
      url,
      timeout: params.timeout ?? 60 * 1000,
    };

    if ('payload' in params) {
      requestParams.payload = params.payload;
    }

    if ('headers' in params) {
      requestParams.headers = params.headers;
    }

    if ('withCredentials' in params) {
      requestParams.withCredentials = params.withCredentials;
    }

    return this.makeRequest(requestParams);
  }
}
